import { apiClient } from '@/shared/api';
import type { ApiResponse } from '@/shared/api';

export type AuditEventType =
  | 'login'
  | 'login_failed'
  | 'logout'
  | 'page_view'
  | 'impersonation'
  | 'feature_flag_change'
  | 'comms_preference_change'
  | 'skill_change_proposed'
  | 'skill_override_change'
  | 'notebook_change'
  | 'texting_setup_change'
  | 'tracked_link_change';

export interface AuditEvent {
  id: string;
  eventType: AuditEventType;
  sessionId: string | null;
  userId: string | null;
  email: string;
  userName: string | null;
  isAgency: boolean;
  practiceId: string | null;
  practiceName: string | null;
  path: string | null;
  label: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  /** Pre-formatted "San Francisco, CA" — null when no GeoIP database is configured. */
  location: string | null;
  createdAt: string;
}

export interface AuditEventsPage {
  events: AuditEvent[];
  /** Row count per event type under the current search, plus an `all` total. */
  counts: Record<string, number>;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditSummary {
  activeUsers24h: number;
  sessions24h: number;
  pageViews24h: number;
  failedLogins24h: number;
  retentionDays: number;
}

export interface AuditSessionDetail {
  sessionId: string;
  userId: string | null;
  userName: string | null;
  email: string;
  isAgency: boolean;
  practiceName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  location: string | null;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  pageViewCount: number;
  trail: AuditEvent[];
}

export interface FetchAuditEventsParams {
  search?: string;
  eventType?: AuditEventType;
  days?: number;
  page?: number;
  limit?: number;
}

export const auditApi = {
  async listEvents(params: FetchAuditEventsParams): Promise<AuditEventsPage> {
    const { data } = await apiClient.get<ApiResponse<AuditEventsPage>>('/audit/events', { params });
    return data.data;
  },

  async getSummary(): Promise<AuditSummary> {
    const { data } = await apiClient.get<ApiResponse<AuditSummary>>('/audit/summary');
    return data.data;
  },

  async getSession(sessionId: string): Promise<AuditSessionDetail> {
    const { data } = await apiClient.get<ApiResponse<AuditSessionDetail>>(
      `/audit/sessions/${sessionId}`,
    );
    return data.data;
  },

  /**
   * Report a page view. Fire-and-forget by design: navigation must never wait
   * on the audit beacon, and a failed beacon must never surface to the user.
   */
  recordPageView(path: string, label?: string): void {
    void apiClient.post('/audit/page-view', { path, label }).catch(() => {
      /* audit beacons are best-effort */
    });
  },

  /** Report an agency user entering a practice's account. */
  recordImpersonation(practiceId: string, practiceName?: string): void {
    void apiClient.post('/audit/impersonation', { practiceId, practiceName }).catch(() => {
      /* audit beacons are best-effort */
    });
  },
};
